import { Button } from '@repo/ui'
import { Loader2, RotateCcw, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { AgentRun } from '@/api/proposals/proposals'
import { proposalBuildNeedsFreshRun } from '@/common/proposalBuildRecovery'
import { useBuildRun } from '@/common/useBuildRun'
import type { UseBuildRunResult } from '@/common/useBuildRun'
import type { ReactNode } from 'react'

const terminalStatuses = new Set<AgentRun['status']>(['succeeded', 'failed', 'cancelled'])

const statusLabels: Record<Exclude<AgentRun['status'], 'succeeded'>, string> = {
  queued: 'Build queued',
  running: 'Building',
  waiting_for_input: 'Waiting for input',
  failed: 'Build failed',
  cancelled: 'Build cancelled'
}

export interface BuildRunStripControls {
  getRun: (runId: number, signal: AbortSignal) => Promise<AgentRun>
  startRun: () => Promise<AgentRun>
  cancelRun: (runId: number) => Promise<AgentRun>
  retryRun: (runId: number) => Promise<AgentRun>
}

export interface BuildRunStripProps {
  initialRun: AgentRun | null
  controls: BuildRunStripControls
  onSettled: () => void
  onChooseSources?: () => void
  onActivityChange?: (active: boolean) => void
  // Optional detail replaces the status row while the tracked run is non-terminal (the
  // lifted ProposalBuildProgress / PresentationBuildProgress cards). It receives the same run
  // object and hook controls the strip renders from, so stop/retry act on exactly one run.
  progressDetail?: (run: AgentRun, buildRun: UseBuildRunResult) => ReactNode
}

export function BuildRunStrip({
  initialRun,
  controls,
  onSettled,
  progressDetail,
  onChooseSources,
  onActivityChange
}: BuildRunStripProps) {
  const buildRun = useBuildRun({
    initialRun,
    getRun: controls.getRun,
    startRun: controls.startRun,
    cancelRun: controls.cancelRun,
    retryRun: controls.retryRun
  })
  const { run, error, clearError, cancel, retry, start, isStarting, isCancelling, isRetrying } = buildRun

  const settledKeyRef = useRef<string | null>(null)
  const onSettledRef = useRef(onSettled)

  useEffect(() => {
    onSettledRef.current = onSettled
  })

  const status = run?.status ?? null
  const runId = run?.runId ?? null

  useEffect(() => {
    onActivityChange?.(isStarting || isRetrying || (status !== null && !terminalStatuses.has(status)))
  }, [status, isStarting, isRetrying, onActivityChange])

  useEffect(() => {
    if (status === null || !terminalStatuses.has(status)) return
    const key = `${String(runId)}:${status}`
    if (settledKeyRef.current === key) return
    settledKeyRef.current = key
    onSettledRef.current()
  }, [status, runId])

  // Success leaves the document as the result, without a permanent status banner.
  // Keep the lifecycle effects above so the document reloads and its controls unlock.
  if (run === null || run.status === 'succeeded') return null

  const cancelling = isCancelling || run.cancelRequested
  const recovering = isStarting || isRetrying
  const recover = proposalBuildNeedsFreshRun(run) ? start : retry
  const showProgress = progressDetail !== undefined && !terminalStatuses.has(run.status)
  const sourceRequired =
    run.status === 'failed' && run.failureCode === 'source_required' && onChooseSources !== undefined
  const visibleError =
    sourceRequired && buildRun.operationError === null && error !== null
      ? 'Choose source content for this proposal in Sources, then resume drafting. You can select files or text already added to the workspace.'
      : error

  return (
    <div className="mx-auto w-full max-w-[46rem] px-5 py-4 sm:px-7">
      {showProgress ? progressDetail(run, buildRun) : null}
      {(!showProgress || visibleError !== null) && (
        <div className="flex flex-wrap items-start gap-3 rounded-md border bg-card px-3 py-3 text-sm">
          {!showProgress && (
            <p role="status" className="pt-1.5 font-medium">
              {statusLabels[run.status]}
            </p>
          )}
          {visibleError !== null && (
            <div className="flex min-w-0 flex-[1_1_16rem] items-start gap-2">
              <p role="alert" className="min-w-0 flex-1 pt-1.5 break-words text-destructive">
                {visibleError}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="size-8 shrink-0"
                onClick={clearError}
                aria-label="Dismiss error"
              >
                <X aria-hidden="true" className="size-4" />
              </Button>
            </div>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {!showProgress &&
              (run.status === 'queued' || run.status === 'running' || run.status === 'waiting_for_input') && (
                <Button type="button" variant="outline" size="sm" onClick={() => void cancel()} disabled={cancelling}>
                  {cancelling ? (
                    <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
                  ) : null}
                  {cancelling ? 'Cancelling...' : 'Cancel'}
                </Button>
              )}
            {sourceRequired ? (
              <Button type="button" size="sm" onClick={onChooseSources}>
                Choose sources
              </Button>
            ) : (
              run.status === 'failed' && (
                <Button type="button" size="sm" onClick={() => void recover()} disabled={recovering}>
                  <RotateCcw
                    aria-hidden="true"
                    className={recovering ? 'size-4 animate-spin motion-reduce:animate-none' : 'size-4'}
                  />
                  {recovering ? 'Retrying...' : 'Retry'}
                </Button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  )
}
